
import * as React from "react"
import { cn } from "@/lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoResize = false, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    
    React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);
    
    const handleResize = React.useCallback(() => {
      if (autoResize && textareaRef.current) {
        // Reset height to auto to get the correct scrollHeight
        textareaRef.current.style.height = 'auto';
        // Set the height to the scrollHeight to fit content
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, [autoResize]);
    
    // Resize on content change
    React.useEffect(() => {
      if (autoResize) {
        handleResize();
        // Add event listener for input events
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.addEventListener('input', handleResize);
          return () => textarea.removeEventListener('input', handleResize);
        }
      }
    }, [autoResize, handleResize]);
    
    // Initial resize after component mounts
    React.useEffect(() => {
      if (autoResize) {
        handleResize();
      }
    }, [autoResize, props.value, handleResize]);
    
    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={textareaRef}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
